import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Dialog } from '@mui/material';
import {
  selectClassPrefix,
  ShareDialogContent,
  StyledSelect,
} from './styles';
import {
  IProps,
  IStateProps,
  IDispatchProps,
  IState,
} from './interfaces';
import toast from '../../../../utils/toast';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogActionsOverride,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import Button from '../../../Shared/VaultButton';
import SelectedAssetsInfo from '../../../Shared/Publish/PublishProfilesDialog/SelectedAssetsInfo/component';
import { AssetHit } from '../../../../api/vault/searchEngine/interfaces';
import { IAppState } from '../../../../state/reducers';
import { getSecurityProfileProductOptions } from '../../../../state/securityProfiles';
import { selectIsUserVaultSuperAdmin, selectUserRole } from '../../../../state/user/userOrgRole';
import {
  toggleShareDialog,
  toggleLoading,
  updateSelectedOptions,
  clearSelectedOptions,
  queryAssetDetails,
  TSelectOption,
} from '../../../../state/shareDialog';
import { resetAssetDetails } from '../../../../state/previewAsset';
import { editAssetMetadata } from '../../../AssetDetails/api/editAssetMetadata';
import { sendEmail } from '../../../../api/vault/email';

// utils
const idFromAsset = (asset: AssetHit): string => asset._id;
const idFromOption = (option: TSelectOption): string => option.value;

class ShareDialog extends PureComponent<IProps, IState> {
  componentDidMount() {
    const { onGetProductOptions } = this.props;
    onGetProductOptions();
  }
  async componentDidUpdate(prevProps: IProps) {
    const {
      isLoading,
      product,
      selectedAssets,
      queryAssetDetailsConnect,
      onGetProductOptions,
    } = this.props;

    const currentAssets = prevProps.selectedAssets.map(idFromAsset);
    const updatedAssets = selectedAssets.map(idFromAsset);
    const joinedAssets = new Set([...currentAssets, ...updatedAssets]);
    const isSelectedAssetsChanged = joinedAssets.size !== currentAssets.length || joinedAssets.size !== updatedAssets.length;

    if (isSelectedAssetsChanged && !isLoading) {
      queryAssetDetailsConnect(selectedAssets);
    }

    if (prevProps.product !== product) {
      onGetProductOptions();
    }
  }

  onExit() {
    const {
      handleExitDialog,
    } = this.props;
    handleExitDialog();
  }

  onChange(selectedOptions: Array<TSelectOption>) {
    const { updateSelectedOptionsConnect } = this.props;
    updateSelectedOptionsConnect(selectedOptions);
  }

  async onSave() {
    const {
      assetDetails,
      selectedOptions,
      teamMap,
      userOrgRole,
      toggleLoadingConnect,
      resetAssetDetailsConnect,
      isVaultSuperAdmin,
    } = this.props;

    const { userVaultRoles: [organization] } = userOrgRole;
    const { orgName } = organization;

    const securityProfiles = selectedOptions.map(idFromOption);
    const updatedAssets = assetDetails.map((asset) => {
      securityProfiles
        .filter((sp) => !asset.securityProfiles.includes(sp))
        .forEach((sp) => {
          const addresses = teamMap[sp];
          if (addresses) {
            addresses.forEach(async (address) => {
              try {
                await sendEmail({
                  toAddresses: [address],
                  fromAddress: null,
                  subject: `Catapult Sports - "${asset.title}" has been shared with you.`,
                  isHtmlBody: true,
                  body: `
                    "${asset.title}" has been shared with you by ${orgName}.
                    &nbsp;Click <a href="${process.env.REACT_APP_SSO_LOGIN_URL}">here</a> to access this content.
                  `,
                });
              } catch (e) {
                if (!isVaultSuperAdmin) {
                  toast(`Error encountered notifying recipient "${address}"`, {
                    type: toast.TYPE.ERROR,
                  });
                }
              }
            });
          }
        });

      return {
        ...asset,
        securityProfiles: _.uniq([...asset.securityProfiles, ...securityProfiles]),
      };
    });

    toggleLoadingConnect(true);

    try {
      const results = await Promise.all(updatedAssets.map(editAssetMetadata));
      // eslint-disable-next-line
      results.forEach(result => {
        if (result.status !== 204) {
          console.error(`[QA] asset "${result.request.data.id}" could not be shared`);
        }
      });

      this.onExit();

      resetAssetDetailsConnect();
    } catch (e) {
      console.error('[QA] error occurred while sharing assets');
    }

    toggleLoadingConnect(false);
  }

  renderAssetField() {
    const {
      selectedOptions,
      productOptions,
    } = this.props;

    return (
      <StyledSelect
        classNamePrefix={selectClassPrefix}
        options={productOptions}
        value={selectedOptions}
        onChange={(opts: unknown) => this.onChange(opts as Array<TSelectOption>)}
        menuIsOpen
        isMulti
      />
    );
  }

  render() {
    const {
      isOpen,
      isLoading,
      selectedAssets,
    } = this.props;

    return (
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="sm"
      >
        <DialogHeader
          data-auto="ShareAssetDialog"
        >
          <DialogHeaderTitle>
            Share Asset(s)
          </DialogHeaderTitle>
        </DialogHeader>
        <ShareDialogContent
          data-auto="SharedDialogContent"
        >
          {!isLoading
            ? this.renderAssetField()
            : 'Loading...'}
          <SelectedAssetsInfo
            selectedAssets={selectedAssets}
            action="Sharing"
            data-auto="SharedAssetItems"
          />
        </ShareDialogContent>
        <DialogActionsOverride>
          <ActionContainer>
            <Button
              variant="plainOnPaper"
              onClick={() => this.onExit()}
              data-auto="ShareCancelBtn"
            >
              Cancel
            </Button>
            <Button
              variant="primaryOnWhite"
              onClick={() => this.onSave()}
              disabled={isLoading}
              data-auto="ShareSaveBtn"
            >
              Save
            </Button>
          </ActionContainer>
        </DialogActionsOverride>
      </Dialog>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpen: state.shareDialog.isOpen,
  isLoading: state.shareDialog?.isLoading ?? false,
  selectedAssets: state.shareDialog?.selectedAssets ?? [],
  selectedOptions: state.shareDialog?.selectedOptions ?? [],
  assetDetails: state.shareDialog?.assetDetails,
  product: state.product?.target,
  productOptions: state.securityProfiles?.productOptions ?? [],
  teamMap: state.shareDialog?.teamMap ?? {},
  userOrgRole: selectUserRole(state),
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  handleExitDialog: toggleShareDialog,
  toggleLoadingConnect: toggleLoading,
  updateSelectedOptionsConnect: updateSelectedOptions,
  clearSelectedOptionsConnect: clearSelectedOptions,
  queryAssetDetailsConnect: queryAssetDetails,
  resetAssetDetailsConnect: resetAssetDetails,
  onGetProductOptions: getSecurityProfileProductOptions,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ShareDialog);
